/**
 * Panneau d'une trace de visiteur (0.36.0, demande de Benoit) : vendre ou déplacer.
 *
 * Une modale à TROIS issues — fermer, déplacer, vendre — et l'ordre des boutons est
 * l'ordre du risque : « Fermer » d'abord (c'est lui que `createModal` focalise, un
 * Entrée réflexe ne vend rien), « Déplacer » ensuite, « Vendre » en dernier avec son
 * MONTANT dans le libellé — le prix est l'information de la décision.
 *
 * Une seule modale réutilisée, rappel remplacé à chaque `ask` : les mêmes raisons que
 * `confirm.ts`, au même endroit.
 */

import { t } from '@/i18n';
import { createButton } from './components';
import { createModal } from './components/overlays';

export type TraceChoice = 'sell' | 'move';

export interface TracePanelHandle {
  /**
   * `sellLabel` à `null` : la vente n'est pas proposée. Un bouton visible qui
   * échouerait en silence est pire qu'une option absente — c'est l'état des
   * décorations payées tant que leur barème de revente (ROADMAP 2.9d) n'est pas livré.
   */
  ask(sellLabel: string | null, onChoice: (choice: TraceChoice) => void): void;
  destroy(): void;
}

export function createTracePanel(): TracePanelHandle {
  const modal = createModal({
    title: t('traces.title'),
    text: t('traces.text'),
    icon: 'flower',
    onClose: () => {
      pending = null;
    },
  });
  document.body.append(modal.el);

  let pending: ((choice: TraceChoice) => void) | null = null;

  const close = createButton({
    label: t('common.close'),
    variant: 'ghost',
    onClick: () => {
      pending = null;
      modal.close();
    },
  });

  const move = createButton({
    label: t('traces.move'),
    variant: 'secondary',
    onClick: () => {
      const run = pending;
      pending = null;
      modal.close();
      run?.('move');
    },
  });

  const sell = createButton({
    label: '',
    variant: 'primary',
    onClick: () => {
      const run = pending;
      pending = null;
      modal.close();
      run?.('sell');
    },
  });
  sell.el.classList.add('trace-panel__sell');
  move.el.classList.add('trace-panel__move');

  modal.actions.append(close.el, move.el, sell.el);

  return {
    ask(sellLabel, onChoice) {
      sell.el.hidden = sellLabel === null;
      sell.setLabel(sellLabel ?? '');
      pending = onChoice;
      modal.open();
    },
    destroy() {
      pending = null;
      modal.destroy();
      modal.el.remove();
    },
  };
}
